import { useCallback, useRef, useState } from "react";

import { SavingStatus } from "./SavingProgressDialog";

/**
 * Estado de envio compartilhado pelo `SavingProgressDialog` (camada 1). Encapsula
 * a máquina `idle → saving → success | error` e adia a ação pós-sucesso (navegar,
 * fechar dialog…) até a animação de sucesso terminar — assim o usuário vê os
 * passos concluírem antes de a tela mudar.
 *
 * Uso típico no hook do formulário:
 *
 * ```ts
 * const saving = useSavingProgress()
 * const enviar = () => {
 *   let result: Entidade | null = null
 *   void saving.run(
 *     async () => { result = await persist(data); return !!result },
 *     () => result && navegar(result.id), // roda em onComplete
 *   )
 * }
 * ```
 *
 * E na tela: `<SavingProgressDialog status={saving.status} onComplete={saving.handleComplete} … />`.
 */
export function useSavingProgress() {
  const [status, setStatus] = useState<SavingStatus | "idle">("idle");
  // Ação a disparar quando a animação de sucesso terminar (navegação/fechamento).
  const afterRef = useRef<(() => void) | null>(null);
  // Trava reentrância enquanto a request roda (sem depender do estado assíncrono).
  const runningRef = useRef(false);

  /**
   * Executa `action` exibindo o progresso. `action` deve resolver `true` no
   * sucesso e `false` (ou lançar) no erro. `onDone` é guardado e só roda em
   * `handleComplete`. Retorna o sucesso para fluxos que precisam reagir (ex.
   * "enviar e criar outro" volta ao 1º passo).
   */
  const run = useCallback(
    async (action: () => Promise<boolean>, onDone?: () => void): Promise<boolean> => {
      if (runningRef.current) return false;
      runningRef.current = true;
      setStatus("saving");

      let ok = false;
      try {
        ok = await action();
      } catch (error) {
        console.error(error);
        ok = false;
      } finally {
        runningRef.current = false;
      }

      if (ok) {
        afterRef.current = onDone ?? null;
        setStatus("success");
      } else {
        setStatus("error");
      }
      return ok;
    },
    [],
  );

  /** Animação de sucesso terminou: volta a `idle` e dispara a ação adiada. */
  const handleComplete = useCallback(() => {
    const fn = afterRef.current;
    afterRef.current = null;
    setStatus("idle");
    fn?.();
  }, []);

  /** Fecha o diálogo (ex. botão do estado de erro) sem disparar a ação adiada. */
  const reset = useCallback(() => {
    afterRef.current = null;
    setStatus("idle");
  }, []);

  return {
    status,
    isOpen: status !== "idle",
    isSaving: status === "saving",
    // O diálogo só aceita 'saving' | 'success' | 'error'; quando `idle` ele está fechado.
    dialogStatus: (status === "idle" ? "saving" : status) as SavingStatus,
    run,
    handleComplete,
    reset,
  };
}

export type UseSavingProgressReturn = ReturnType<typeof useSavingProgress>;
